/**
 * Image Loader Utility
 * 
 * This utility automatically loads images from the assets/images folder structure.
 * When you add images to the appropriate folders, they will be automatically available.
 * 
 * Usage:
 * - Place images in: src/assets/images/{category}/{filename}
 * - Images are automatically discovered and can be accessed via getImage()
 * 
 * Supported formats: jpg, jpeg, png, svg, webp
 */

// Helper to generate placeholder image URL
const getPlaceholder = (width = 400, height = 300, text = '') => {
  const encodedText = encodeURIComponent(text || 'Image Placeholder');
  return `https://via.placeholder.com/${width}x${height}?text=${encodedText}`;
};

// Initialize image contexts for each category using require.context
// require.context MUST be called at module level (not conditionally) for webpack to process it
// These will be available at build time and automatically include new images

// eslint-disable-next-line
const brandContext = require.context('../assets/images/brand', false, /\.(jpg|jpeg|png|svg|webp)$/i);
// eslint-disable-next-line
const heroContext = require.context('../assets/images/hero', false, /\.(jpg|jpeg|png|svg|webp)$/i);
// eslint-disable-next-line
const teamContext = require.context('../assets/images/team', false, /\.(jpg|jpeg|png|svg|webp)$/i);
// eslint-disable-next-line
const projectsContext = require.context('../assets/images/projects', false, /\.(jpg|jpeg|png|svg|webp)$/i);
// eslint-disable-next-line
const galleryContext = require.context('../assets/images/gallery', true, /\.(jpg|jpeg|png|svg|webp)$/i);
// eslint-disable-next-line
const testimonialsContext = require.context('../assets/images/testimonials', false, /\.(jpg|jpeg|png|svg|webp)$/i);
// eslint-disable-next-line
const servicesContext = require.context('../assets/images/services', false, /\.(jpg|jpeg|png|svg|webp)$/i);
// eslint-disable-next-line
const clientsContext = require.context('../assets/images/clients', false, /\.(jpg|jpeg|png|svg|webp)$/i);
// eslint-disable-next-line
const homeContext = require.context('../assets/images/home', false, /\.(jpg|jpeg|png|svg|webp)$/i);

const imageContexts = {
  brand: brandContext,
  hero: heroContext,
  team: teamContext,
  projects: projectsContext,
  gallery: galleryContext,
  testimonials: testimonialsContext,
  services: servicesContext,
  clients: clientsContext,
  home: homeContext,
};

/**
 * Get all images from a category
 * @param {string} category - The image category (brand, hero, team, etc.)
 * @returns {Array} Array of image objects with name and url
 */
export const getImagesByCategory = (category) => {
  const context = imageContexts[category];
  if (!context) {
    return [];
  }

  try {
    return context.keys().map((item) => {
      const image = context(item);
      return {
        name: item.replace('./', '').replace(/\.(jpg|jpeg|png|svg|webp)$/i, ''),
        url: image.default || image,
        fullPath: item,
      };
    });
  } catch (error) {
    console.warn(`Error loading images from category ${category}:`, error);
    return [];
  }
};

/**
 * Get a specific image by name from a category
 * @param {string} category - The image category
 * @param {string} name - The image name (without extension)
 * @param {object} options - Options for placeholder fallback
 * @returns {string} Image URL or placeholder
 */
export const getImage = (category, name, options = {}) => {
  const { width = 400, height = 300, placeholderText = '' } = options;
  const context = imageContexts[category];

  if (!context) {
    return getPlaceholder(width, height, placeholderText || `${category}/${name}`);
  }

  try {
    // Try to find the image (case-insensitive, any extension)
    // Supports both "Featured1" and "Featured/Featured1" for nested paths
    const keys = context.keys();
    const imageKey = keys.find((key) => {
      const keyName = key.replace('./', '').replace(/\.(jpg|jpeg|png|svg|webp)$/i, '');
      const keyBasename = keyName.split('/').pop();
      return (
        keyName.toLowerCase() === name.toLowerCase() ||
        keyBasename.toLowerCase() === name.toLowerCase()
      );
    });

    if (imageKey) {
      const image = context(imageKey);
      return image.default || image;
    }
  } catch (error) {
    console.warn(`Error loading image ${category}/${name}:`, error);
  }

  // Return placeholder if image not found
  return getPlaceholder(width, height, placeholderText || `${category}/${name}`);
};

/**
 * Get image by index from a category (useful for arrays)
 * @param {string} category - The image category
 * @param {number} index - The index (0-based)
 * @param {object} options - Options for placeholder fallback
 * @returns {string} Image URL or placeholder
 */
export const getImageByIndex = (category, index, options = {}) => {
  const images = getImagesByCategory(category);
  if (images[index]) {
    return images[index].url;
  }
  const { width = 400, height = 300, placeholderText = '' } = options;
  return getPlaceholder(width, height, placeholderText || `${category}[${index}]`);
};

/**
 * Get all available image categories
 * @returns {Array} Array of category names
 */
export const getImageCategories = () => {
  return Object.keys(imageContexts).filter((cat) => imageContexts[cat] !== null);
};

/**
 * Parse folder name to extract heading and work description
 * e.g. "127 TPH WHRB boiler erection photo at Shyam Metalic, Sambalpur, Odisha"
 *   → { title: "127 TPH WHRB Boiler Erection", work: "Boiler Erection", location: "Shyam Metalic, Sambalpur, Odisha" }
 */
const parseProjectFolderName = (folder) => {
  const cleaned = folder.replace(/\.$/, '').replace(/\s*photo\s*/gi, ' ').trim();
  let title = cleaned;
  let work = '';
  let location = '';

  if (cleaned.includes(' at ')) {
    const [workPart, locPart] = cleaned.split(/\s+at\s+/).map((s) => s.trim());
    title = workPart;
    location = locPart;
    work = workPart.replace(/^\d+\s*(x\s*\d+\s*)?/, '').trim(); // Remove leading capacity
  } else if (cleaned.includes(' - ')) {
    const [workPart, locPart] = cleaned.split(/\s+-\s+/).map((s) => s.trim());
    title = workPart;
    location = locPart;
    work = workPart.replace(/^\d+\s*(x\s*\d+\s*)?/, '').trim();
  } else {
    work = cleaned.replace(/^\d+\s*(x\s*\d+\s*)?/, '').trim();
  }

  return { title, work, location };
};

/**
 * Get gallery projects grouped by folder (excludes Featured)
 * Each project has heading, work description, location, and images
 * @returns {Array} Array of { id, title, work, location, images: [{ id, url }] }
 */
export const getGalleryProjects = () => {
  const allGallery = getImagesByCategory('gallery');
  const byFolder = {};

  allGallery.forEach((img) => {
    if (img.name.startsWith('Featured/')) return;

    const parts = img.name.split('/');
    const folder = parts.length > 1 ? parts.slice(0, -1).join('/') : 'Projects';
    const fileName = parts[parts.length - 1];

    if (!byFolder[folder]) byFolder[folder] = [];
    byFolder[folder].push({ ...img, fileName });
  });

  return Object.entries(byFolder).map(([folder, images], idx) => {
    const { title, work, location } = parseProjectFolderName(folder);
    return {
      id: idx + 1,
      title,
      work,
      location,
      images: images.map((img, i) => ({ id: `${idx + 1}-${i + 1}`, url: img.url })),
    };
  });
};

/**
 * Flat list of all gallery images (for backward compatibility / lightbox)
 * @returns {Array} Array of { id, title, category, image, projectId }
 */
export const getGalleryCollectionImages = () => {
  return getGalleryProjects().flatMap((project) =>
    project.images.map((img, i) => ({
      id: img.id,
      title: project.title,
      work: project.work,
      location: project.location,
      category: project.work || 'Projects',
      image: img.url,
      projectId: project.id,
    }))
  );
};

// Export placeholder generator for direct use
export { getPlaceholder };
